<template>
  <transition name="fade">
  <div class="resource-detail" v-if="resource">
    <!-- 返回按钮 -->
    <el-button 
      icon="el-icon-arrow-left" 
      @click="goBack" 
      class="back-btn"
      type="text">
      返回列表
    </el-button>

    <div class="detail-container">
      <!-- 左侧主要内容 -->
      <div class="detail-main">
        <!-- 置顶丝带 -->
        <div class="top-ribbon" v-if="resource.isTop === 1">
          <i class="el-icon-star-on"></i> 推荐
        </div>
        
        <!-- 资源头部 -->
        <div class="resource-header">
          <div class="header-badges">
            <div class="fixed-tags">
              <el-tag v-if="resource.requiredMembership > 0" type="warning" effect="dark">
                <i class="el-icon-trophy"></i> 
                {{ ['', '高级会员专享', '专业会员专享'][resource.requiredMembership] }}
              </el-tag>
              <el-tag v-if="resource.isFree === 1" type="success">免费资源</el-tag>
              <el-tag v-else type="warning">付费资源 ¥{{ resource.price }}</el-tag>
              <el-tag type="info">{{ getResourceType(resource.resourceType) }}</el-tag>
            </div>
            <div class="user-tags">
              <el-tag
                v-for="tag in (resource.tags ? resource.tags.split(',') : [])"
                :key="tag"
                size="small"
                type="success"
                class="resource-tag"
              >
                {{ tag }}
              </el-tag>
            </div>
          </div>
          
          <h1 class="resource-title">{{ resource.title }}</h1>
          
          <div class="resource-meta">
            <span class="meta-item">
              <i class="el-icon-view"></i>
              浏览 {{ resource.viewCount }}
            </span>
            <span class="meta-item">
              <i class="el-icon-download"></i>
              下载 {{ resource.downloadCount }}
            </span>
            <span class="meta-item">
              <i class="el-icon-star-off"></i>
              点赞 {{ resource.likeCount }}
            </span>
            <span class="meta-item">
              <i class="el-icon-time"></i>
              {{ formatDate(resource.createTime) }}
            </span>
          </div>
        </div>

        <!-- 资源介绍区域 -->
        <div class="intro-section">
          <h3 class="section-title">
            <i class="el-icon-document"></i>
            资源介绍
          </h3>
          
          <!-- 介绍内容与封面图左右布局 -->
          <div class="intro-content-wrapper">
            <!-- 左侧封面图（支持点击放大） -->
            <div class="intro-cover" v-if="resource.coverImage">
              <el-image 
                :src="resource.coverImage" 
                :preview-src-list="[resource.coverImage]"
                fit="cover"
                @error="handleImageError">
                <div slot="placeholder" class="image-slot">
                  <i class="el-icon-loading"></i> 加载中...
                </div>
                <div slot="error" class="image-slot">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              <div class="image-tip">
                <i class="el-icon-zoom-in"></i> 点击查看大图
              </div>
            </div>
            
            <!-- 右侧介绍文字 -->
            <div class="intro-text">
              <div class="resource-description">{{ resource.description }}</div>
            </div>
          </div>
        </div>

        <!-- 详细内容 -->
        <div class="content-section" v-if="resource.content">
          <h3 class="section-title">
            <i class="el-icon-tickets"></i>
            详细说明
          </h3>
          <div class="resource-content" v-html="resource.content" @click="handleContentImageClick"></div>
        </div>

        <!-- 资源下载区域 -->
        <div class="download-section">
          <h3 class="section-title">
            <i class="el-icon-download"></i>
            资源下载
          </h3>
          
          <div class="download-content">
            <!-- 网盘分享链接按钮 -->
            <div v-if="resource.shareType" class="share-download-section">
              <!-- 能下载时显示按钮 -->
              <el-button 
                v-if="canDownload"
                type="primary" 
                size="large"
                @click="openShareLink"
                :icon="getShareIcon(resource.shareType)"
                class="share-download-btn"
                round>
                {{ getShareButtonText(resource.shareType) }}
              </el-button>
              <!-- 不能下载时显示提示 -->
              <el-alert
                v-else
                type="warning"
                :closable="false"
                show-icon>
                <template slot="title">
                  <div style="font-size: 14px; line-height: 1.6;">
                    {{ downloadTip || '您今日下载免费资源额度已用完' }}
                    <el-link type="primary" @click="navigateToMembership" style="margin-left: 8px;">
                      升级会员
                    </el-link>
                  </div>
                </template>
              </el-alert>
            </div>
            
            <!-- 文件列表 -->
            <div class="file-list" v-if="files.length > 0">
              <div class="file-section-divider" v-if="resource.shareType">
                <el-divider content-position="left">或直接下载</el-divider>
              </div>
              <div v-for="file in files" :key="file.id" class="file-item">
                <div class="file-icon">
                  <i class="el-icon-document"></i>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.fileName }}</div>
                  <div class="file-size">{{ formatSize(file.fileSize) }}</div>
                </div>
                <!-- 不能下载时显示禁用按钮 -->
                <div v-if="!canDownload" class="download-disabled-tip">
                  <el-tag type="warning" size="small">额度已用完</el-tag>
                </div>
                <!-- 能下载时显示下载按钮 -->
                <el-button 
                  v-else
                  type="primary" 
                  size="small" 
                  icon="el-icon-download"
                  @click="downloadFile(file)"
                  class="file-download-btn"
                  round>
                  下载
                </el-button>
              </div>
            </div>
            
            <!-- 无文件且无分享链接 -->
            <el-empty v-if="!resource.shareType && files.length === 0" description="暂无资源" :image-size="80"></el-empty>
            
            <!-- 下载提示信息 -->
            <div class="download-tips" v-if="downloadCheckResult && isLoggedIn">
              <div class="tip-item" v-if="downloadCheckResult.dailyLimit !== -1">
                <i class="el-icon-info"></i>
                温馨提醒：今日剩余下载次数 {{ downloadCheckResult.remainingDownloads }} 次，每日凌晨自动重置
              </div>
              <div class="tip-item" v-if="downloadCheckResult.extraDownloads > 0">
                <i class="el-icon-circle-plus-outline"></i>
                您还有 {{ downloadCheckResult.extraDownloads }} 次额外下载机会（永久有效）
              </div>
              <div class="tip-item success" v-if="downloadCheckResult.dailyLimit === -1">
                <i class="el-icon-success"></i>
                尊贵的会员用户，您享有无限次下载权益
              </div>
            </div>
            <!-- 未登录提示 -->
            <div class="download-tips" v-if="!isLoggedIn">
              <div class="tip-item warning">
                <i class="el-icon-warning"></i>
                请
                <el-link type="primary" @click="$router.push('/login')" :underline="false">登录</el-link>
                后下载资源
              </div>
            </div>
          </div>
        </div>

        <!-- 互动区域 -->
        <div class="interaction-section">
          <div class="support-tip">
            <div class="support-content">
              <i class="el-icon-star-on support-icon"></i>
              <div class="support-text-wrapper">
                <span class="support-text">如果能持续帮到您，在您方便的时候，开通会员就是最好的支持方式～ 🙏</span>
                <div class="support-subtext">您的支持是我们持续提供优质资源的最大动力</div>
              </div>
            </div>
            <el-button type="primary" size="medium" round class="upgrade-btn" @click.stop="navigateToMembership">
              <i class="el-icon-medal"></i>
              升级会员
            </el-button>
          </div>
          
          <div class="action-bar">
            <el-button 
              :type="resource.isLiked ? 'primary' : 'default'"
              :icon="resource.isLiked ? 'el-icon-star-on' : 'el-icon-star-off'"
              @click="toggleLike"
              round>
              点赞 {{ resource.likeCount || 0 }}
            </el-button>
            
            <el-button 
              icon="el-icon-share"
              @click="shareResource"
              round>
              分享
            </el-button>
            
            <el-button 
              :type="resource.isCollected ? 'warning' : 'default'"
              :icon="resource.isCollected ? 'el-icon-star-on' : 'el-icon-star-off'"
              @click="toggleCollect"
              round>
              收藏
            </el-button>
          </div>
        </div>

        <!-- 相关推荐 -->
        <div class="related-section">
          <h3 class="section-title">
            <i class="el-icon-files"></i>
            相关推荐 ({{ relatedResources.length }})
          </h3>
          <div v-if="relatedResources.length > 0" class="related-list">
            <div 
              v-for="item in relatedResources" 
              :key="item.id" 
              class="related-item"
              @click="goToResource(item.id)">
              <div class="related-cover">
                <img v-if="item.coverImage" :src="item.coverImage" alt="封面" />
                <div v-else class="cover-placeholder">
                  <i class="el-icon-picture-outline"></i>
                </div>
                <el-tag
                  v-if="item.requiredMembership > 0"
                  type="warning"
                  size="mini"
                  class="vip-badge">
                  VIP
                </el-tag>
              </div>
              <div class="related-info">
                <h4 class="related-title">{{ item.title }}</h4>
                <p class="related-desc">{{ item.description }}</p>
                <div class="related-meta">
                  <span class="meta-tag">
                    <i class="el-icon-view"></i>
                    {{ item.viewCount || 0 }}
                  </span>
                  <span class="meta-tag">
                    <i class="el-icon-download"></i>
                    {{ item.downloadCount || 0 }}
                  </span>
                  <span class="meta-tag">
                    <i class="el-icon-star-off"></i>
                    {{ item.likeCount || 0 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else style="padding: 20px; text-align: center; color: #909399;">
            暂无相关推荐
          </div>
        </div>

        <!-- 评论区域 -->
        <div class="comment-section">
          <h3 class="section-title">
            <i class="el-icon-chat-dot-round"></i>
            评论区 ({{ commentTotal }})
          </h3>

          <!-- 发表评论 -->
          <div class="comment-input-box">
            <el-input
              v-model="commentContent"
              type="textarea"
              :rows="3"
              :placeholder="isLoggedIn ? '说说你的看法吧~' : '说说你的看法吧~（需要登录）'"
              maxlength="500"
              show-word-limit
              :disabled="!isLoggedIn">
            </el-input>
            <div class="comment-input-actions">
              <el-popover
                placement="bottom-start"
                v-model="showEmojiPicker"
                trigger="manual"
                popper-class="emoji-popover"
                append-to-body>
                <div class="emoji-picker">
                  <div 
                    v-for="(emoji, index) in emojis" 
                    :key="emoji + '-' + index" 
                    class="emoji-item" 
                    @click="insertEmoji(emoji)">
                    {{ emoji }}
                  </div>
                </div>
                <el-button 
                  slot="reference"
                  type="text" 
                  size="mini" 
                  @click="showEmojiPicker = !showEmojiPicker"
                  class="emoji-toggle-btn"
                  :class="{ 'active': showEmojiPicker }"
                  title="添加表情">😃</el-button>
              </el-popover>
            </div>
            <div class="comment-actions">
              <el-button 
                type="primary" 
                size="small" 
                @click="submitComment"
                :disabled="!commentContent.trim() || submittingComment"
                :loading="submittingComment">
                发表评论
              </el-button>
            </div>
          </div>

          <!-- 评论列表 -->
          <div class="comment-list" v-loading="loadingComments">
            <div v-if="comments.length === 0" class="empty-comments">
              <i class="el-icon-chat-line-round"></i>
              <p>暂无评论，快来发表第一条评论吧~</p>
            </div>
            <div v-else>
              <div v-for="comment in comments" :key="comment.id" class="comment-item">
                <div class="comment-avatar">
                  <img :src="comment.userAvatar || defaultAvatar" alt="头像" />
                </div>
                <div class="comment-content-wrapper">
                  <div class="comment-header">
                    <span class="comment-user">{{ comment.userNickname }}</span>
                    <span class="comment-time">{{ formatTime(comment.createTime) }}</span>
                  </div>
                  <div class="comment-content">{{ comment.content }}</div>
                  <div class="comment-actions-bar">
                    <span class="comment-action" @click="showReplyInput(comment)">
                      <i class="el-icon-chat-dot-round"></i>
                      回复 ({{ comment.replyCount }})
                    </span>
                    <span class="comment-action" v-if="isMyComment(comment.userId)" @click="deleteComment(comment.id)">
                      <i class="el-icon-delete"></i>
                      删除
                    </span>
                  </div>

                  <!-- 回复输入框 -->
                  <div v-if="replyingCommentId === comment.id" class="reply-input-box">
                    <el-input
                      v-model="replyContent"
                      type="textarea"
                      :rows="2"
                      placeholder="写下你的回复~"
                      maxlength="500"
                      show-word-limit>
                    </el-input>
                    <div class="reply-input-actions">
                      <el-popover
                        placement="bottom-start"
                        v-model="showReplyEmojiPicker"
                        trigger="manual"
                        popper-class="emoji-popover"
                        append-to-body>
                        <div class="emoji-picker">
                          <div 
                            v-for="(emoji, index) in emojis" 
                            :key="emoji + '-' + index" 
                            class="emoji-item" 
                            @click="insertReplyEmoji(emoji)">
                            {{ emoji }}
                          </div>
                        </div>
                        <el-button 
                          slot="reference"
                          type="text" 
                          size="mini" 
                          @click="showReplyEmojiPicker = !showReplyEmojiPicker"
                          class="emoji-toggle-btn"
                          :class="{ 'active': showReplyEmojiPicker }"
                          title="添加表情">😃</el-button>
                      </el-popover>
                    </div>
                    <div class="reply-actions">
                      <el-button size="mini" @click="cancelReply">取消</el-button>
                      <el-button 
                        type="primary" 
                        size="mini" 
                        @click="submitReply(comment.id)"
                        :disabled="!replyContent.trim()"
                        :loading="submittingReply">
                        发送
                      </el-button>
                    </div>
                  </div>

                  <!-- 回复列表 -->
                  <div v-if="comment.replies && comment.replies.length > 0" class="reply-list">
                    <div v-for="reply in comment.replies" :key="reply.id" class="reply-item">
                      <div class="reply-avatar">
                        <img :src="reply.userAvatar || defaultAvatar" alt="头像" />
                      </div>
                      <div class="reply-content-wrapper">
                        <div class="reply-header">
                          <span class="reply-user">{{ reply.userNickname }}</span>
                          <span class="reply-time">{{ formatTime(reply.createTime) }}</span>
                        </div>
                        <div class="reply-content">{{ reply.content }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div v-if="commentTotal > commentPageSize" class="comment-pagination">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="commentTotal"
              :page-size="commentPageSize"
              :current-page="commentPage"
              @current-change="handleCommentPageChange">
            </el-pagination>
          </div>
        </div>
      </div>

      <!-- 右侧信息栏 -->
      <div class="detail-sidebar">
        <!-- 热门下载 -->
        <div class="hot-download-card" v-if="topDownloadResources.length > 0">
          <div class="card-title">
            <span class="fire-icon">🔥</span>
            热门下载
          </div>
          <div class="hot-download-list">
            <div 
              v-for="(item, index) in topDownloadResources" 
              :key="item.id" 
              class="hot-download-item"
              @click="goToResource(item.id)">
              <div class="rank-badge" :class="'rank-' + (index + 1)">
                <span class="rank-number">{{ index + 1 }}</span>
              </div>
              <div class="hot-item-cover">
                <img v-if="item.coverImage" :src="item.coverImage" alt="封面" />
                <div v-else class="cover-placeholder">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </div>
              <div class="hot-item-info">
                <div class="hot-item-title">
                  <span class="title-text">{{ item.title }}</span>
                  <span class="hot-tag" v-if="index === 0">HOT</span>
                </div>
                <div class="hot-item-meta">
                  <span class="meta-item">
                    <i class="el-icon-view"></i>
                    {{ item.viewCount || 0 }}
                  </span>
                  <span class="meta-item download-count">
                    <i class="el-icon-download"></i>
                    {{ item.downloadCount || 0 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 温馨提示 -->
        <div class="tips-card">
          <div class="tips-title">
            <i class="el-icon-warning"></i>
            温馨提示
          </div>
          <ul class="tips-list">
            <li v-if="isLoggedIn && downloadCheckResult && downloadCheckResult.dailyLimit !== -1">
              温馨提醒：今日剩余下载次数 {{ downloadCheckResult.remainingDownloads }} 次，每日凌晨自动重置
            </li>
            <li v-if="isLoggedIn && downloadCheckResult && downloadCheckResult.extraDownloads > 0">
              您还有 {{ downloadCheckResult.extraDownloads }} 次额外下载机会（永久有效）
            </li>
            <li v-if="isLoggedIn && downloadCheckResult && downloadCheckResult.dailyLimit === -1">
              尊贵的会员用户，您享有无限次下载权益
            </li>
            <li v-if="!isLoggedIn">
              <el-link type="primary" @click="$router.push('/login')" :underline="false">登录</el-link>
              后即可查看下载次数
            </li>
            <li>请勿用于商业用途</li>
            <li>下载前请确认资源适用性</li>
            <li>如有问题请及时反馈</li>
          </ul>
        </div>

        <!-- 版权声明 -->
        <div class="copyright-card">
          <div class="copyright-title">
            <i class="el-icon-info"></i>
            版权声明
          </div>
          <div class="copyright-content">
            <div class="copyright-item">
              <strong>网站名称：</strong>{{ siteConfig?.site_name || siteConfig?.site_title || '知享汇资源库' }}
            </div>
            <div class="copyright-item">
              <strong>永久网址：</strong>
              <el-link :href="siteConfig?.site_url || 'http://localhost:8081'" target="_blank" type="primary">
                {{ siteConfig?.site_url || 'http://localhost:8081' }}
              </el-link>
            </div>
            <div class="copyright-item copyright-welcome">
              欢迎您访问{{ siteConfig?.site_name || siteConfig?.site_title || '知享汇资源库' }}！敬请扫码加入我们的微信粉丝群，第一时间获取最新资源与动态。
            </div>
            <div class="copyright-item">
              本站部分文章内容来源于网络，旨在供用户学习与参考。如内容涉及侵权，请通过底部联系方式联系站长进行删除。
            </div>
            <div class="copyright-item">
              本站所有资源仅代表相关作者或来源方立场，本站不对其观点及真实性作任何担保或认可。
            </div>
            <div class="copyright-item">
              严禁以任何形式发布或转载任何违法信息。如有发现，请立即向站长举报。
            </div>
            <div class="copyright-item">
              本站资源主要存储于云盘，若出现链接失效，请联系我们，我们将及时更新。
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分享对话框 -->
    <el-dialog
      title="分享资源"
      :visible.sync="shareDialogVisible"
      width="500px"
      center>
      <div class="share-dialog-content">
        <div class="share-methods">
          <div class="share-item" @click="shareToWechat">
            <div class="share-icon wechat">
              <i class="el-icon-chat-dot-round"></i>
            </div>
            <div class="share-label">微信</div>
          </div>
          
          <div class="share-item" @click="shareToQQ">
            <div class="share-icon qq">
              <i class="el-icon-message"></i>
            </div>
            <div class="share-label">QQ</div>
          </div>
          
          <div class="share-item" @click="shareToWeibo">
            <div class="share-icon weibo">
              <i class="el-icon-share"></i>
            </div>
            <div class="share-label">微博</div>
          </div>
          
          <div class="share-item" @click="copyShareLink">
            <div class="share-icon copy">
              <i class="el-icon-document-copy"></i>
            </div>
            <div class="share-label">复制链接</div>
          </div>
        </div>
        
        <div class="share-link-box">
          <el-input v-model="shareUrl" readonly size="small">
            <template slot="append">
              <el-button @click="copyShareLink" icon="el-icon-document-copy">复制</el-button>
            </template>
          </el-input>
        </div>
      </div>
    </el-dialog>
  </div>
  </transition>
</template>

<script>
export default {
  name: 'ResourceDetail',
  data() {
    return {
      resource: null,
      files: [],
      canDownload: true,
      downloadTip: '',
      downloadCheckResult: null,  // 下载权限检查结果
      siteConfig: null,  // 网站配置
      shareDialogVisible: false,  // 分享对话框显示
      relatedResources: [],  // 相关推荐资源
      topDownloadResources: [],  // 热门下载资源
      liking: false,  // 点赞请求中
      collecting: false,  // 收藏请求中
      // 评论相关
      comments: [],  // 评论列表
      commentTotal: 0,  // 评论总数
      commentPage: 1,  // 当前页
      commentPageSize: 10,  // 每页条数
      commentContent: '',  // 评论内容
      replyContent: '',  // 回复内容
      replyingCommentId: null,  // 正在回复的评论 ID
      loadingComments: false,  // 加载评论中
      submittingComment: false,  // 提交评论中
      submittingReply: false,  // 提交回复中
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
      // 表情相关
      showEmojiPicker: false,  // 显示表情选择器
      showReplyEmojiPicker: false,  // 显示回复表情选择器
      emojis: [
        // 笑脸和情感
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
        '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
        '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫',
        '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬',
        '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢',
        '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸',
        
        // 手势和身体部位
        '👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
        '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍',
        '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝',
        '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂',
        '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁', '👅',
        '👄', '👶', '🧒', '👦', '👧', '🧑', '👱', '👨', '🧔', '👨‍🦰',
        '👩', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨‍🦳', '👩‍🦳', '👨‍🦲', '👩‍🦲', '🦸', '🦹',
        
        // 动物
        '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
        '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒',
        '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇',
        '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜',
        '🦟', '🦗', '🕷', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙',
        '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋',
        
        // 食物
        '🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🥭', '🍎', '🍏',
        '🍐', '🍑', '🍒', '🍓', '🫐', '🥝', '🍅', '🫒', '🥥', '🥑',
        '🍆', '🥔', '🥕', '🌽', '🌶', '🫑', '🥒', '🥬', '🥦', '🧄',
        '🧅', '🍄', '🥜', '🌰', '🍞', '🥐', '🥖', '🫓', '🥨', '🥯',
        '🥞', '🧇', '🧀', '🍖', '🍗', '🥩', '🥓', '🍔', '🍟', '🍕',
        '🌭', '🥪', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚', '🍳', '🥘',
        '🍲', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙',
        
        // 活动和运动
        '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
        '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳',
        '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '⛸',
        '🥌', '🎿', '⛷', '🏂', '🪂', '🏋️', '🤼', '🤸', '🤸', '⛹️',
        '🤺', '🤾', '🏌️', '🏇', '🧘', '🏄', '🏊', '🤽', '🚣', '🧗',
        '🚵', '🚴', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖', '🏵', '🎗',
        
        // 旅行和地点
        '🚗', '🚕', '🚙', '🚌', '🚎', '🏎', '🚓', '🚑', '🚒', '🚐',
        '🚛', '🚜', '🛴', '🚲', '🛵', '🏍', '🛺', '🚨', '🚔',
        '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝',
        '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚉', '✈️', '🛫',
        '🛬', '🛩', '💺', '🛰', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤',
        '🛥', '🛳', '⛴', '🚢', '⚓', '🪝', '🚧', '⛽', '🚏', '🚦',
        '🚥', '🗺', '🗿', '🗽', '🗼', '🏰', '🏯', '🏟', '🎡', '🎢',
        
        // 物品和符号
        '📱', '📲', '💻', '⌨️', '🖥', '🖨', '🖱', '🖲', '🕹', '🗜',
        '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽',
        '🎞', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚', '🎛',
        '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋', '🔌',
        '💡', '🔦', '🕯', '🪔', '🧯', '🛢', '💸', '💵', '💴', '💶',
        '💷', '💰', '💳', '💎', '⚖️', '🔧', '🔨', '⚒', '🛠', '⛏',
        '🔩', '⚙️', '🧰', '🪛', '🧪', '🧫', '🧬', '🔬', '🔭', '📡',
        
        // 符号和标志
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
        '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
        '✝️', '☪️', '🕉', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
        '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
        '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
        '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️',
        '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️',
        
        // 天气和自然
        '☀️', '🌤', '⛅', '🌥', '☁️', '🌦', '🌧', '⛈', '🌩', '⚡',
        '❄️', '☃️', '⛄', '🌫️', '🌈', '🌂', '☂️', '☔', '⛱', '⚡',
        '❄️', '☃️', '⛄', '☄️', '🔥', '💧', '🌊', '🎄', '🎆', '🎇',
        '🧨', '✨', '🎈', '🎉', '🎊', '🎋', '🎍', '🎎', '🎏', '🎐',
        '🎑', '🧧', '🎀', '🎁', '🎗', '🎟', '🎫', '🎖', '🏆', '🏅',
        '🥇', '🥈', '🥉', '⚽', '⚾', '🏀', '🏐', '🏈', '🏉', '🎾',
        '🎯', '🎮', '🎲', '♟', '🎭', '🎨', '🧵', '🧶', '🎼', '🎵'
      ]
    }
  },
  mounted() {
    this.loadSiteConfig()
    this.loadResourceDetail()
    this.loadResourceFiles()
    this.checkDownloadPermission()  // 检查下载权限
    this.loadComments()  // 加载评论
    this.loadTopDownloadResources()  // 加载热门下载资源
  },
  // watch: {
  //   // 监听资源数据变化，动态设置页面标题
  //   'resource.title': {
  //     handler(newTitle) {
  //       if (newTitle) {
  //         const baseTitle = this.siteConfig?.site_title || this.siteConfig?.site_name || '知享汇资源库'
  //         document.title = `${newTitle} - ${baseTitle}`
  //       }
  //     },
  //     immediate: true
  //   }
  // },
  computed: {
    shareUrl() {
      return window.location.href
    },
    // 是否已登录
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },
    // 当前用户 ID
    currentUserId() {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          return user.id
        } catch (e) {
          return null
        }
      }
      return null
    }
  },
  methods: {
    // 处理详细内容中的图片点击（使用 Element UI 的图片预览）
    handleContentImageClick(e) {
      if (e.target.tagName === 'IMG') {
        const imgSrc = e.target.src
        
        // 使用 Element UI 的 ImageViewer 组件
        const ImageViewer = () => import('element-ui/packages/image/src/image-viewer')
        ImageViewer().then(module => {
          const ImageViewerComponent = module.default
          const viewer = new (this.$root.constructor)({
            el: document.createElement('div'),
            render: h => h(ImageViewerComponent, {
              props: {
                urlList: [imgSrc],
                onClose: () => {
                  viewer.$destroy()
                  viewer.$el.remove()
                },
                zIndex: 3000
              }
            })
          })
          document.body.appendChild(viewer.$el)
        })
      }
    },
    async loadSiteConfig() {
      try {
        const res = await this.$axios.get('/config/map')
        if (res.data.code === 200 && res.data.data) {
          this.siteConfig = res.data.data
        }
      } catch (error) {
        console.error('加载网站配置失败:', error)
      }
    },
    async loadResourceDetail() {
      const id = this.$route.params.id
      const res = await this.$axios.get(`/resource/${id}`)
      if (res.data.code === 200) {
        this.resource = res.data.data
        console.log('加载的资源数据:', this.resource)
        console.log('资源类型 resourceType:', this.resource.resourceType)
        
        // 动态设置 SEO Meta 标签
        this.updatePageMeta()
        
        // 加载点赞和收藏状态
        this.loadInteractionStatus()
        // 加载相关推荐
        this.loadRelatedResources()
      }
    },
    
    // 动态更新页面 Meta 标签（SEO 优化）
    updatePageMeta() {
      if (!this.resource) return
      
      const baseTitle = this.siteConfig?.site_title || this.siteConfig?.site_name || 'ShareFree'
      const resourceTitle = this.resource.title
      const resourceDesc = this.resource.description || ''
      const resourceTags = this.resource.tags || ''
      
      // 设置页面标题
      document.title = `${resourceTitle} - ${baseTitle}`
      
      // 设置 description
      this.setMetaTag('name', 'description', resourceDesc.substring(0, 150))
      
      // 设置 keywords
      const keywords = resourceTags ? resourceTags.replace(/,/g, ',') : ''
      this.setMetaTag('name', 'keywords', keywords)
      
      // 设置 Open Graph 标签
      this.setMetaTag('property', 'og:title', resourceTitle)
      this.setMetaTag('property', 'og:description', resourceDesc.substring(0, 150))
      this.setMetaTag('property', 'og:url', window.location.href)
      if (this.resource.coverImage) {
        this.setMetaTag('property', 'og:image', this.resource.coverImage)
      }
    },
    
    // 设置 Meta 标签的工具方法
    setMetaTag(attr, key, content) {
      if (!content) return
      
      let element = document.querySelector(`meta[${attr}="${key}"]`)
      if (element) {
        element.setAttribute('content', content)
      } else {
        element = document.createElement('meta')
        element.setAttribute(attr, key)
        element.setAttribute('content', content)
        document.head.appendChild(element)
      }
    },
    async loadResourceFiles() {
      const id = this.$route.params.id
      const res = await this.$axios.get(`/resource/${id}/files`)
      if (res.data.code === 200) {
        this.files = res.data.data
      }
    },
    async checkDownloadPermission() {
      // 未登录时不检查权限
      if (!this.isLoggedIn) {
        this.canDownload = false
        return
      }
      
      try {
        const id = this.$route.params.id
        const res = await this.$axios.get(`/membership/check-download/${id}`)
        
        if (res.data.code === 200) {
          this.downloadCheckResult = res.data.data
          this.canDownload = res.data.data.canDownload
          
          if (!this.canDownload) {
            this.downloadTip = res.data.data.message || '下载受限'
          }
        } else {
          this.canDownload = false
          this.downloadTip = res.data.message || res.data.msg || '权限检查失败'
        }
      } catch (error) {
        console.error('检查下载权限失败:', error)
        this.canDownload = false
        this.downloadTip = '权限检查失败，请刷新页面重试'
      }
    },
    async downloadFile(file) {
      try {
        // 调用后端接口验证权限并获取下载地址
        const res = await this.$axios.post(`/resource/${this.resource.id}/download`, {
          fileUrl: file.fileUrl
        })
        
        if (res.data.code === 200) {
          // 成功：跳转到下载地址
          const downloadUrl = res.data.data.downloadUrl
          window.open(downloadUrl, '_blank')
          this.$message.success('下载开始')
          
          // 下载后重新检查权限（更新剩余次数）
          setTimeout(() => {
            this.checkDownloadPermission()
          }, 1000)
        } else {
          // 失败：显示错误信息并引导升级
          this.$confirm(res.data.msg || '下载失败', '提示', {
            confirmButtonText: '升级会员',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            if (this.$route.path !== '/membership') {
              this.$router.push('/membership').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  console.error(err)
                }
              })
            }
          }).catch(() => {})
          
          // 更新界面状态
          this.canDownload = false
          this.downloadTip = res.data.msg
        }
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请稍后重试')
      }
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-CN')
    },
    getResourceType(type) {
      const map = { 0: '软件', 1: '网站', 2: '视频教程', 3: '图文教程', 4: '其他' }
      return map[type] || '未知'
    },
    goBack() {
      // 直接返回资源列表（首页）
      if (this.$route.path !== '/') {
        this.$router.push('/').catch(err => {
          // 忽略NavigationDuplicated错误，但重新抛出其他错误
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },
    handleImageError(e) {
      // 图片加载失败时隐藏封面区域
      e.target.style.display = 'none'
    },
    getShareButtonText(type) {
      const textMap = {
        baidu: '百度网盘下载',
        quark: '夸克网盘下载',
        aliyun: '阿里云盘下载',
        pan123: '123网盘下载',
        website: '访问网站',
        other: '网盘下载'
      }
      return textMap[type] || '网盘下载'
    },
    getShareIcon(type) {
      const iconMap = {
        baidu: 'el-icon-coin',
        quark: 'el-icon-star-off',
        aliyun: 'el-icon-cloudy',
        pan123: 'el-icon-files',
        website: 'el-icon-link',
        other: 'el-icon-link'
      }
      return iconMap[type] || 'el-icon-link'
    },
    async openShareLink() {
      try {
        // 调用后端接口验证权限并获取分享链接
        const res = await this.$axios.post(`/resource/${this.resource.id}/download`, {})
        
        if (res.data.code === 200) {
          // 成功：从接口返回结果中获取分享链接
          const shareLink = res.data.data.shareLink
          const sharePassword = res.data.data.sharePassword
          
          if (shareLink) {
            // 打开分享链接
            window.open(shareLink, '_blank')
            
            // 如果有密码，显示提示
            if (sharePassword) {
              this.$message({
                message: `提取码：${sharePassword}，已复制到剪贴板`,
                type: 'success',
                duration: 5000,
                showClose: true
              })
              
              // 复制密码到剪贴板
              const textarea = document.createElement('textarea')
              textarea.value = sharePassword
              textarea.style.position = 'fixed'
              textarea.style.opacity = '0'
              document.body.appendChild(textarea)
              textarea.select()
              try {
                document.execCommand('copy')
              } catch (err) {
                console.error('复制失败:', err)
              }
              document.body.removeChild(textarea)
            } else {
              this.$message.success('已打开下载链接')
            }
          } else {
            this.$message.error('未获取到分享链接')
          }
          
          // 下载后重新检查权限（更新剩余次数）
          setTimeout(() => {
            this.checkDownloadPermission()
          }, 1000)
        } else {
          // 失败：更新界面状态
          this.canDownload = false
          this.downloadTip = res.data.message || res.data.msg
          this.$message.warning(this.downloadTip)
        }
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请稍后重试')
      }
    },
    goMembership() {
      this.navigateToMembership()
    },
    // 新增方法：确保跳转到会员页面
    navigateToMembership() {
      console.log('调用 navigateToMembership 方法')
      // 使用原生 JavaScript 跳转作为备选方案
      window.location.href = '/membership'
    },
    toggleLike() {
      // 防抖处理：如果正在请求中，忽略后续点击
      if (this.liking) return
      this.liking = true
      
      // 调用后端接口
      const id = this.$route.params.id
      this.$axios.post(`/user/interaction/like/toggle/${id}`)
        .then(res => {
          if (res.data.code === 200) {
            this.resource.isLiked = res.data.data.isLiked
            this.resource.likeCount = res.data.data.likeCount
            this.$message.success(res.data.data.message)
          }
        })
        .catch(error => {
          console.error('点赞失败:', error)
          this.$message.error('操作失败，请先登录')
        })
        .finally(() => {
          this.liking = false
        })
    },
    shareResource() {
      this.shareDialogVisible = true
    },
    shareToWechat() {
      this.$message.info('请扫描二维码分享到微信，或复制链接后发送给好友')
    },
    shareToQQ() {
      const title = this.resource?.title || '资源分享'
      const url = encodeURIComponent(window.location.href)
      const shareUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${url}&title=${encodeURIComponent(title)}`
      window.open(shareUrl, '_blank')
    },
    shareToWeibo() {
      const title = this.resource?.title || '资源分享'
      const url = encodeURIComponent(window.location.href)
      const shareUrl = `http://service.weibo.com/share/share.php?url=${url}&title=${encodeURIComponent(title)}`
      window.open(shareUrl, '_blank')
    },
    copyShareLink() {
      const url = window.location.href
      const textarea = document.createElement('textarea')
      textarea.value = url
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制到剪贴板！')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      
      document.body.removeChild(textarea)
    },
    toggleCollect() {
      // 防抖处理：如果正在请求中，忽略后续点击
      if (this.collecting) return
      this.collecting = true
      
      // 调用后端接口
      const id = this.$route.params.id
      this.$axios.post(`/user/interaction/collection/toggle/${id}`)
        .then(res => {
          if (res.data.code === 200) {
            this.resource.isCollected = res.data.data.isCollected
            this.$message.success(res.data.data.message)
          }
        })
        .catch(error => {
          console.error('收藏失败:', error)
          this.$message.error('操作失败，请先登录')
        })
        .finally(() => {
          this.collecting = false
        })
    },
    async loadInteractionStatus() {
      try {
        const id = this.$route.params.id
        const res = await this.$axios.get(`/user/interaction/status/${id}`)
        if (res.data.code === 200) {
          this.resource.isLiked = res.data.data.isLiked
          this.resource.isCollected = res.data.data.isCollected
        }
      } catch (error) {
        // 未登录或其他错误，忽略
        console.log('加载互动状态失败:', error)
      }
    },
    async loadRelatedResources() {
      try {
        if (!this.resource || this.resource.resourceType === null || this.resource.resourceType === undefined) {
          console.log('无法加载相关推荐：resource或resourceType为空', this.resource)
          return
        }
        
        console.log('开始加载相关推荐，资源类型:', this.resource.resourceType)
        
        const res = await this.$axios.get('/resource/page', {
          params: {
            resourceType: this.resource.resourceType,
            current: 1,
            size: 6
          }
        })
        
        console.log('相关推荐接口返回:', res.data)
        
        if (res.data.code === 200) {
          // 过滤掉当前资源，只取前4个
          const filtered = res.data.data.records
            .filter(item => item.id !== this.resource.id)
          
          console.log('过滤后的资源数量:', filtered.length)
          
          this.relatedResources = filtered.slice(0, 4)
          
          console.log('最终显示的相关推荐:', this.relatedResources)
        }
      } catch (error) {
        console.error('加载相关推荐失败:', error)
      }
    },
    async loadTopDownloadResources() {
      try {
        const res = await this.$axios.get('/resource/top-downloads')
        if (res.data.code === 200) {
          this.topDownloadResources = res.data.data || []
        }
      } catch (error) {
        console.error('加载热门下载资源失败:', error)
      }
    },
    goToResource(id) {
      // 跳转到资源详情页，并刷新页面
      this.$router.push(`/resource/${id}`).then(() => {
        // 重新加载数据
        window.location.reload()
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },
    // 加载评论列表
    async loadComments() {
      try {
        this.loadingComments = true
        const resourceId = this.$route.params.id
        const res = await this.$axios.get(`/comment/list/${resourceId}`, {
          params: {
            current: this.commentPage,
            size: this.commentPageSize
          }
        })
        
        if (res.data.code === 200) {
          this.comments = res.data.data.records || []
          this.commentTotal = res.data.data.total || 0
        }
      } catch (error) {
        console.error('加载评论失败:', error)
      } finally {
        this.loadingComments = false
      }
    },
    // 发表评论
    async submitComment() {
      if (!this.isLoggedIn) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      
      if (!this.commentContent.trim()) {
        this.$message.warning('评论内容不能为空')
        return
      }
      
      try {
        this.submittingComment = true
        const resourceId = this.$route.params.id
        const res = await this.$axios.post('/comment/add', {
          resourceId: resourceId,
          content: this.commentContent
        })
        
        if (res.data.code === 200) {
          this.$message.success('评论发表成功')
          this.commentContent = ''
          // 重新加载评论列表
          this.commentPage = 1
          await this.loadComments()
        } else {
          this.$message.error(res.data.message || '评论发表失败')
        }
      } catch (error) {
        console.error('发表评论失败:', error)
        this.$message.error('评论发表失败')
      } finally {
        this.submittingComment = false
      }
    },
    // 显示回复输入框
    showReplyInput(comment) {
      if (!this.isLoggedIn) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      this.replyingCommentId = comment.id
      this.replyContent = ''
    },
    // 取消回复
    cancelReply() {
      this.replyingCommentId = null
      this.replyContent = ''
    },
    // 提交回复
    async submitReply(parentId) {
      if (!this.replyContent.trim()) {
        this.$message.warning('回复内容不能为空')
        return
      }
      
      try {
        this.submittingReply = true
        const resourceId = this.$route.params.id
        const res = await this.$axios.post('/comment/add', {
          resourceId: resourceId,
          content: this.replyContent,
          parentId: parentId
        })
        
        if (res.data.code === 200) {
          this.$message.success('回复成功')
          this.cancelReply()
          // 重新加载评论列表
          await this.loadComments()
        } else {
          this.$message.error(res.data.message || '回复失败')
        }
      } catch (error) {
        console.error('回复失败:', error)
        this.$message.error('回复失败')
      } finally {
        this.submittingReply = false
      }
    },
    // 删除评论
    async deleteComment(commentId) {
      try {
        await this.$confirm('确定要删除这条评论吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await this.$axios.delete(`/comment/delete/${commentId}`)
        
        if (res.data.code === 200) {
          this.$message.success('删除成功')
          // 重新加载评论列表
          await this.loadComments()
        } else {
          this.$message.error(res.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除评论失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    // 分页处理
    handleCommentPageChange(page) {
      this.commentPage = page
      this.loadComments()
      // 滚动到评论区域
      this.$nextTick(() => {
        const commentSection = document.querySelector('.comment-section')
        if (commentSection) {
          commentSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    // 判断是否是自己的评论
    isMyComment(userId) {
      return this.currentUserId && this.currentUserId === userId
    },
    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      // 小于1分钟
      if (diff < 60000) {
        return '刚刚'
      }
      // 小于1小时
      if (diff < 3600000) {
        return Math.floor(diff / 60000) + '分钟前'
      }
      // 小于24小时
      if (diff < 86400000) {
        return Math.floor(diff / 3600000) + '小时前'
      }
      // 小于7天
      if (diff < 604800000) {
        return Math.floor(diff / 86400000) + '天前'
      }
      // 超过7天，显示具体日期
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    // 插入表情到评论内容
    insertEmoji(emoji) {
      this.commentContent += emoji
      this.showEmojiPicker = false
    },
    // 插入表情到回复内容
    insertReplyEmoji(emoji) {
      this.replyContent += emoji
      this.showReplyEmojiPicker = false
    }
  }
}
</script>

<style scoped>
.resource-detail {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 20px;
}

.back-btn {
  margin-bottom: 10px !important;
  font-size: 14px !important;
  color: #606266 !important;
  transition: all 0.3s !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 6px 12px !important; /* 增加内边距 */
  border-radius: 22px !important; /* 稍微增加圆角 */
  font-weight: 500 !important; /* 默认字重 */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1) !important; /* 添加轻微阴影 */
  background: transparent !important; /* 确保背景透明 */
  border: 1px solid transparent !important; /* 确保边框透明 */
}

.back-btn:hover {
  color: #409EFF !important;
  transform: translateX(-4px) !important;
  background: rgba(64, 158, 255, 0.1) !important;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2) !important;
}

.detail-container {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 16px;
  align-items: start;
}

/* 左侧主要内容 */
.detail-main {
  background: white;
  border-radius: 16px;
  padding: 35px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 158, 255, 0.08);
  position: relative;
  overflow: hidden;
}

.detail-main:hover {
  box-shadow: 0 10px 40px rgba(64, 158, 255, 0.1);
  transform: translateY(-2px);
}

.resource-header {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 2px solid #f0f2f5;
  position: relative;
}

/* 置顶丝带 */
.top-ribbon {
  position: absolute;
  top: 9px;
  right: -19px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  padding: 1.5px 24px;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.35);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.5px;
  transform: rotate(45deg);
  animation: ribbonFloat 3s ease-in-out infinite;
  border-radius: 2px;
}

.top-ribbon::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 50%);
  pointer-events: none;
}

.top-ribbon i {
  font-size: 12px;
  animation: ribbonStar 2s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

@keyframes ribbonFloat {
  0%, 100% {
    transform: rotate(45deg) translateY(0);
  }
  50% {
    transform: rotate(45deg) translateY(-2px);
  }
}

@keyframes ribbonStar {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.2) rotate(180deg);
  }
}

.resource-header::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 70px;
  height: 2px;
  background: linear-gradient(90deg, #409EFF 0%, #667eea 100%);
  border-radius: 2px;
}

.header-badges {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center; /* 垂直居中 */
  justify-content: space-between; /* 两端对齐 */
}

.fixed-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-start; /* 左对齐 */
}

.user-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end; /* 右对齐 */
}

.header-badges >>> .el-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  line-height: 1;
  border-radius: 16px;
  font-weight: 500;
  white-space: nowrap;
}
.header-badges >>> .el-tag i {
  font-size: 14px;
  margin-right: 4px;
}
@media (max-width: 768px) {
  .header-badges >>> .el-tag {
    padding: 4px 10px;
    font-size: 11px;
    border-radius: 14px;
  }
}

.resource-title {
  font-size: 30px;
  font-weight: 800;
  color: #303133;
  margin: 0 0 16px 0;
  line-height: 1.3;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #303133 0%, #606266 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.resource-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  color: #909399;
  font-size: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 20px;
  transition: all 0.3s;
}

.meta-item:hover {
  background: linear-gradient(135deg, #e8f4ff 0%, #d9ebff 100%);
  color: #409EFF;
  transform: translateY(-2px);
}

.meta-item i {
  font-size: 16px;
}

/* 资源介绍区域 */
.intro-section {
  margin-bottom: 28px;
}

.intro-section .section-title {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 18px 0;
  padding-bottom: 14px;
  border-bottom: 3px solid transparent;
  background: linear-gradient(white, white) padding-box,
              linear-gradient(90deg, #409EFF 0%, #667eea 100%) border-box;
  border-bottom: 3px solid;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.intro-section .section-title::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #409EFF 0%, #667eea 100%);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.intro-section .section-title i {
  color: #409EFF;
  font-size: 22px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* 介绍内容与封面图左右布局 */
.intro-content-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* 左侧封面图 */
.intro-cover {
  flex: 1;
  min-width: 0;
  width: 50%;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf0 100%);
  padding: 14px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  position: relative;
  transition: all 0.4s ease;
  cursor: pointer;
}

/* 右侧介绍文字 */
.intro-text {
  flex: 1;
  min-width: 0;
  width: 50%;
}

.intro-text .resource-description {
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
  padding: 18px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 12px;
  border-left: 4px solid #409EFF;
  box-shadow: 0 3px 10px rgba(0,0,0,0.04);
  position: relative;
  overflow: hidden;
  height: 100%;
  white-space: pre-wrap; /* 保持换行符和空格 */
  word-wrap: break-word; /* 自动换行 */
  word-break: break-word; /* 单词换行 */
  overflow-wrap: break-word; /* 溢出换行 */
}

.intro-text .resource-description::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #409EFF 0%, #667eea 100%);
}

.intro-text .resource-description::after {
  content: '"';
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 50px;
  color: rgba(64, 158, 255, 0.08);
  font-family: Georgia, serif;
  line-height: 1;
}

.intro-cover:hover {
  box-shadow: 0 10px 28px rgba(64, 158, 255, 0.15);
  transform: translateY(-3px);
}

.intro-cover::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 14px;
  padding: 2px;
  background: linear-gradient(135deg, #409EFF, #667eea);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.4s;
}

.intro-cover:hover::before {
  opacity: 0.3;
}

/* Element UI Image 组件样式调整 */
.intro-cover >>> .el-image {
  width: 100%;
  height: auto;
  max-height: 300px; /* 增加最大高度 */
  border-radius: 10px;
  display: block;
  cursor: zoom-in;
}

.intro-cover >>> .el-image img {
  width: 100%;
  height: auto;
  max-height: 300px; /* 增加最大高度 */
  object-fit: cover; /* 改为cover以更好地填充容器 */
  border-radius: 10px;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: block;
}

.intro-cover:hover >>> .el-image img {
  transform: scale(1.08) rotate(1deg);
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px; /* 匹配新的最大高度 */
  background: #f5f7fa;
  color: #909399;
  font-size: 14px;
}

.image-tip {
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  opacity: 0;
  transition: opacity 0.3s;
}

.intro-cover:hover .image-tip {
  opacity: 1;
}

/* 详细内容中的图片可点击放大 */
.resource-content >>> img {
  cursor: zoom-in;
  transition: all 0.3s;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.resource-content >>> img:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.2);
}

.content-section {
  margin-bottom: 28px;
}

/* 资源下载区域 */
.download-section {
  margin-bottom: 24px;
}

.download-content {
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid #e8eaf0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
}

.download-content .share-download-section {
  margin-bottom: 16px;
  text-align: center;
}

.share-download-btn {
  min-width: 200px;
  max-width: 300px;
  width: auto !important;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.share-download-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
}

.download-content .file-list {
  margin-top: 16px;
}

.download-content .file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;
  border: 2px solid #f0f2f5;
  justify-content: space-between;
}

.download-content .file-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #409EFF 0%, #667eea 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

.download-content .file-info {
  flex: 1;
  min-width: 0;
}

.download-content .file-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-content .file-size {
  font-size: 12px;
  color: #909399;
}

.download-content .file-download-btn {
  flex-shrink: 0;
  padding: 8px 16px;
  font-size: 13px;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.download-tips {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px dashed #e8eaf0;
}

.download-tips .tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border-radius: 10px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
  border-left: 4px solid #409EFF;
}

.download-tips .tip-item.success {
  border-left-color: #67C23A;
  color: #67C23A;
}

.download-tips .tip-item.warning {
  border-left-color: #E6A23C;
  color: #E6A23C;
  background: #fdf6ec;
}

.download-tips .tip-item i {
  font-size: 16px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 20px 0;
  padding-bottom: 16px;
  border-bottom: 3px solid #f0f2f5;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #409EFF 0%, #667eea 100%);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.section-title i {
  color: #409EFF;
  font-size: 24px;
}

.resource-description {
  font-size: 15px;
  color: #606266;
  line-height: 2;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 10px;
  border-left: 5px solid #409EFF;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}

.resource-content {
  font-size: 15px;
  color: #606266;
  line-height: 1.8;
  white-space: pre-wrap; /* 保持换行符和空格 */
  word-wrap: break-word; /* 自动换行 */
  word-break: break-word; /* 单词换行 */
  overflow-wrap: break-word; /* 溢出换行 */
}

/* 富文本内容样式 */
.resource-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 16px 0;
  display: block;
}

.resource-content >>> p {
  margin: 12px 0;
  white-space: pre-wrap; /* 保持段落内的换行符 */
}

.resource-content >>> h1,
.resource-content >>> h2,
.resource-content >>> h3,
.resource-content >>> h4,
.resource-content >>> h5,
.resource-content >>> h6 {
  margin: 20px 0 12px 0;
  color: #303133;
  font-weight: 600;
}

.resource-content >>> ul,
.resource-content >>> ol {
  padding-left: 24px;
  margin: 12px 0;
  white-space: pre-wrap; /* 保持列表内的换行符 */
}

.resource-content >>> blockquote {
  border-left: 4px solid #409EFF;
  padding-left: 16px;
  margin: 16px 0;
  color: #606266;
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 4px;
  white-space: pre-wrap; /* 保持引用块内的换行符 */
}

.resource-content >>> code {
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  white-space: pre-wrap; /* 保持代码内的换行符 */
}

.resource-content >>> pre {
  background: #282c34;
  color: #abb2bf;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 16px 0;
  white-space: pre-wrap; /* 保持预格式文本内的换行符 */
}

.resource-content >>> pre code {
  background: transparent;
  padding: 0;
  color: inherit;
  white-space: pre-wrap; /* 保持预格式代码内的换行符 */
}

/* 互动区域 */
.interaction-section {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 2px solid #f0f2f5;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
}

.support-tip {
  background: linear-gradient(135deg, #e8f4ff 0%, #d9ebff 100%);
  padding: 24px;
  border-radius: 18px;
  border-left: 5px solid #409EFF;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: space-between;
  font-size: 15px;
  color: #409EFF;
  font-weight: 600;
  line-height: 1.7;
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.2);
  transition: all 0.3s;
  position: relative;
  /* 移除 overflow: hidden 避免影响点击事件 */
  /* 确保容器本身可以接收点击事件 */
  pointer-events: auto;
}

.support-tip::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.08) 0%, rgba(102, 126, 234, 0.08) 100%);
  opacity: 0;
  transition: opacity 0.3s;
  /* 确保伪元素不影响点击事件 */
  pointer-events: none;
}

.support-tip:hover {
  box-shadow: 0 8px 28px rgba(64, 158, 255, 0.3);
  transform: translateY(-3px);
}

.support-tip:hover::before {
  opacity: 1;
}

.support-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  /* 确保内容区域不影响按钮点击 */
  pointer-events: none;
}

.support-content > * {
  /* 重新启用子元素的点击事件 */
  pointer-events: auto;
}

.support-icon {
  font-size: 28px;
  color: #409EFF;
  animation: heartbeat 2s ease-in-out infinite;
  flex-shrink: 0;
}

.support-text-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.support-text {
  font-weight: 600;
  font-size: 16px;
  color: #2c5caa;
  overflow: hidden;
  text-overflow: ellipsis;
}

.support-subtext {
  font-size: 13px;
  font-weight: 500;
  color: #608bc9;
  opacity: 0.9;
}

.upgrade-btn {
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 26px;
  background: linear-gradient(135deg, #409EFF 0%, #667eea 100%) !important;
  border: none;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  align-items: center;
  gap: 8px;
  /* 确保按钮在最上层 */
  z-index: 10;
  position: relative;
}

.upgrade-btn:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
}

.upgrade-btn i {
  font-size: 18px;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

.action-bar {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  /* 确保与上方元素有足够的间距 */
  margin-top: 20px;
  position: relative;
  z-index: 1;
}

.action-bar >>> .el-button {
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 26px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.action-bar >>> .el-button:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.3);
}

.action-bar >>> .el-button--primary {
  background: linear-gradient(135deg, #409EFF 0%, #667eea 100%);
  border: none;
}

.action-bar >>> .el-button--warning {
  background: linear-gradient(135deg, #E6A23C 0%, #f5a623 100%);
  border: none;
}

/* 相关推荐 */
.related-section {
  margin-top: 25px; /* 减少上方间距 */
  padding-top: 20px; /* 减少上方内边距 */
  /* 移除上方的横线 */
  /* border-top: 3px solid #f0f2f5; */
  position: relative;
}

/* 移除 related-section 的 ::before 伪元素，避免重复的横线 */
/* .related-section::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 0;
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #409EFF 0%, #667eea 100%);
  border-radius: 2px;
} */

.related-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 15px; /* 减少上方间距 */
}

.related-item {
  display: flex;
  gap: 20px;
  background: white;
  border-radius: 18px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 2px solid #f0f2f5;
  box-shadow: 0 6px 20px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}

.related-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(180deg, #409EFF 0%, #667eea 100%);
  transform: scaleY(0);
  transition: transform 0.4s;
}

.related-item:hover {
  transform: translateY(-8px) scale(1.03);
  box-shadow: 0 16px 40px rgba(64, 158, 255, 0.25);
  border-color: #409EFF;
  background: linear-gradient(135deg, #f8fcff 0%, #e8f4ff 100%);
}

.related-item:hover::before {
  transform: scaleY(1);
}

.related-cover {
  position: relative;
  width: 150px;
  height: 112px;
  flex-shrink: 0;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(135deg, #e8eaf0 0%, #d9dce3 100%);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
}

.related-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.related-item:hover .related-cover img {
  transform: scale(1.2) rotate(3deg);
}

.related-cover .cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  color: #dcdfe6;
}

.related-cover .vip-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #E6A23C 0%, #f5a623 100%);
  box-shadow: 0 3px 10px rgba(230, 162, 60, 0.4);
}

.related-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.related-title {
  font-size: 17px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 12px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.3s;
  position: relative;
}

.related-item:hover .related-title {
  color: #409EFF;
}

.related-title::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 30px;
  height: 2px;
  background: #409EFF;
  border-radius: 1px;
  transition: width 0.3s;
}

.related-item:hover .related-title::after {
  width: 100%;
}

.related-desc {
  font-size: 14px;
  color: #909399;
  margin: 0 0 16px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.7;
  flex: 1;
}

.related-meta {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #909399;
}

.related-meta .meta-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f5f7fa;
  border-radius: 14px;
  transition: all 0.3s;
}

.related-item:hover .related-meta .meta-tag {
  background: rgba(64, 158, 255, 0.1);
  color: #409EFF;
}

.related-meta .meta-tag i {
  font-size: 16px;
}

/* 分享对话框 */
.share-dialog-content {
  padding: 20px 0;
}

.share-methods {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.share-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.share-item:hover {
  transform: translateY(-4px);
}

.share-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.share-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
}

.share-icon.wechat {
  background: linear-gradient(135deg, #09BB07 0%, #00D100 100%);
}

.share-icon.qq {
  background: linear-gradient(135deg, #12B7F5 0%, #00A6ED 100%);
}

.share-icon.weibo {
  background: linear-gradient(135deg, #E6162D 0%, #FF6A7A 100%);
}

.share-icon.copy {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.share-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.share-link-box {
  padding-top: 20px;
  border-top: 2px solid #f0f2f5;
}

/* 右侧边栏 */
.detail-sidebar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: sticky;
  top: 80px;
}

.download-card,
.tips-card,
.copyright-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  transition: all 0.3s;
  margin-bottom: 6px;
}

.tips-card,
.copyright-card {
  margin-top: 0;
}

.download-card:hover,
.tips-card:hover,
.copyright-card:hover {
  box-shadow: 0 6px 30px rgba(0,0,0,0.12);
  transform: translateY(-2px);
}

.card-header {
  font-size: 17px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 3px solid #409EFF;
  position: relative;
}

.card-header::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, #409EFF 0%, #667eea 100%);
  border-radius: 2px;
}

.card-header i {
  color: #409EFF;
  font-size: 20px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 12px;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.file-item:hover {
  background: linear-gradient(135deg, #e8f4ff 0%, #d9ebff 100%);
  transform: translateX(6px);
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.file-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  font-size: 20px;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

.file-size {
  font-size: 12px;
  color: #909399;
}

.download-disabled-tip {
  flex-shrink: 0;
}

/* 网盘分享下载区域 */
.share-download-section {
  margin-bottom: 20px;
}

.file-section-divider {
  margin: 16px 0;
}

/* 温馨提示 */
.tips-card {
  background: linear-gradient(135deg, #fff9e6 0%, #fef3d9 100%);
  border: 2px solid rgba(230, 162, 60, 0.2);
}

.tips-title {
  font-size: 15px;
  font-weight: 700;
  color: #E6A23C;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tips-title i {
  font-size: 18px;
  animation: warningPulse 2s ease-in-out infinite;
}

@keyframes warningPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tips-list li {
  font-size: 13px;
  color: #606266;
  line-height: 2;
  padding-left: 16px;
  position: relative;
}

.tips-list li:before {
  content: '•';
  position: absolute;
  left: 0;
  color: #E6A23C;
  font-weight: bold;
}

/* 热门下载 */
.hot-download-card {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(255, 87, 87, 0.12);
  margin-bottom: 6px;
  border: 2px solid rgba(255, 87, 87, 0.2);
  position: relative;
  overflow: hidden;
}

.hot-download-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255, 87, 87, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #ff5757;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.fire-icon {
  font-size: 20px;
  display: inline-block;
  animation: fireAnimation 1.5s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(255, 87, 87, 0.3));
}

@keyframes fireAnimation {
  0%, 100% {
    transform: scale(1) rotate(-5deg);
  }
  50% {
    transform: scale(1.1) rotate(5deg);
  }
}

.hot-download-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hot-download-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.hot-download-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, #ff5757 0%, #ff8787 100%);
  opacity: 0;
  transition: opacity 0.3s;
}

.hot-download-item:hover {
  background: linear-gradient(135deg, #fff9f9 0%, #fff2f2 100%);
  border-color: #ff5757;
  box-shadow: 0 6px 20px rgba(255, 87, 87, 0.2);
  transform: translateX(6px) scale(1.02);
}

.hot-download-item:hover::before {
  opacity: 1;
}

.rank-badge {
  width: 36px;
  height: 36px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  transition: all 0.3s ease;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.rank-badge::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: inherit;
  clip-path: polygon(
    50% 0%, 
    61% 35%, 
    98% 35%, 
    68% 57%, 
    79% 91%, 
    50% 70%, 
    21% 91%, 
    32% 57%, 
    2% 35%, 
    39% 35%
  );
  transition: all 0.3s ease;
}

.hot-download-item:hover .rank-badge::before {
  transform: scale(1.15) rotate(10deg);
  filter: brightness(1.1);
}

.rank-number {
  position: relative;
  z-index: 1;
}

/* 封面图片样式 */
.hot-item-cover {
  width: 70px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.hot-item-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.hot-download-item:hover .hot-item-cover img {
  transform: scale(1.15);
}

.hot-item-cover .cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 20px;
}

.hot-item-info {
  flex: 1;
  min-width: 0;
}

.rank-badge.rank-1 {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%) !important;
  filter: drop-shadow(0 3px 8px rgba(255, 165, 0, 0.5)) drop-shadow(0 0 15px rgba(255, 215, 0, 0.3));
}

.rank-badge.rank-1::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  animation: shimmer 2s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

.rank-badge.rank-2 {
  background: linear-gradient(135deg, #E0E0E0 0%, #B0B0B0 100%) !important;
  filter: drop-shadow(0 3px 8px rgba(176, 176, 176, 0.4)) drop-shadow(0 0 12px rgba(224, 224, 224, 0.3));
}

.rank-badge.rank-2::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, transparent 70%);
  border-radius: 50%;
}

.rank-badge.rank-3 {
  background: linear-gradient(135deg, #CD7F32 0%, #9A5324 100%) !important;
  filter: drop-shadow(0 3px 8px rgba(205, 127, 50, 0.4)) drop-shadow(0 0 12px rgba(154, 83, 36, 0.3));
}

.rank-badge.rank-3::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  border-radius: 50%;
}

.rank-badge.rank-4,
.rank-badge.rank-5,
.rank-badge.rank-6,
.rank-badge.rank-7,
.rank-badge.rank-8,
.rank-badge.rank-9,
.rank-badge.rank-10 {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8e8e93 0%, #636366 100%);
  box-shadow: 0 2px 8px rgba(142, 142, 147, 0.35);
}

.rank-badge.rank-4::before,
.rank-badge.rank-5::before,
.rank-badge.rank-6::before,
.rank-badge.rank-7::before,
.rank-badge.rank-8::before,
.rank-badge.rank-9::before,
.rank-badge.rank-10::before {
  display: none;
}

.hot-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.title-text {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  transition: color 0.3s;
  line-height: 1.4;
}

.hot-download-item:hover .title-text {
  color: #ff5757;
}

.hot-tag {
  background: linear-gradient(135deg, #ff5757 0%, #ff8787 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(255, 87, 87, 0.3);
  animation: hotTagPulse 1.5s ease-in-out infinite;
}

@keyframes hotTagPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 6px rgba(255, 87, 87, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(255, 87, 87, 0.5);
  }
}

.hot-item-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  align-items: center;
}

.hot-item-meta .meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s;
}

.hot-item-meta i {
  font-size: 14px;
}

.hot-item-meta .download-count {
  color: #ff5757;
  font-weight: 600;
}

.hot-download-item:hover .hot-item-meta .meta-item {
  transform: translateY(-1px);
}

/* 版权声明 */
.copyright-card {
  background: linear-gradient(135deg, #e8f4ff 0%, #d9ebff 100%);
  border: 2px solid rgba(64, 158, 255, 0.2);
}

.copyright-title {
  font-size: 15px;
  font-weight: 700;
  color: #409EFF;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.copyright-title i {
  font-size: 18px;
}

.copyright-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.copyright-item {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
}

.copyright-item strong {
  color: #303133;
  font-weight: 600;
}

.copyright-welcome {
  background: white;
  padding: 14px;
  border-radius: 10px;
  border-left: 4px solid #409EFF;
  color: #409EFF;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

@media (max-width: 768px) {
  /* 根容器 - 确保从屏幕边缘开始 */
  .resource-detail {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }
  
  /* 主容器 */
  .detail-container {
    grid-template-columns: 1fr;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
    box-sizing: border-box;
  }
  
  /* 侧边栏 */
  .detail-sidebar {
    position: static;
    padding: 12px !important;
    margin: 0 !important;
    width: 100% !important;
    box-sizing: border-box;
  }
  
  /* 主内容区 */
  .detail-main {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
    box-sizing: border-box;
  }
  
  /* 返回按钮 */
  .back-btn {
    margin: 10px 12px !important;
  }
  
  /* 置顶丝带 */
  .top-ribbon {
    margin: 0 !important;
  }
  
  /* 资源头部 */
  .resource-header {
    padding: 16px 12px !important;
    margin: 0 !important;
    box-sizing: border-box;
  }
  
  .header-badges {
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 12px 0 !important;
  }
  
  .header-badges >>> .el-tag {
    font-size: 11px;
    padding: 4px 8px;
  }
  
  .resource-title {
    font-size: 20px;
    line-height: 1.4;
    word-break: break-word;
    padding: 0 !important;
    margin: 0 0 12px 0 !important;
  }
  
  .resource-meta {
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 !important;
    margin: 0 !important;
  }
  
  .meta-item {
    font-size: 12px;
    padding: 4px 10px;
  }
  
  /* 介绍区域 */
  .intro-section {
    margin: 16px 0 0 0 !important;
    padding: 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    word-break: break-word;
    padding: 12px !important;
    margin: 0 !important;
    background: rgba(102, 126, 234, 0.05);
    border-left: 3px solid #667eea;
  }
  
  .intro-content-wrapper {
    flex-direction: column;
    padding: 12px !important;
    margin: 0 !important;
  }
  
  .intro-cover {
    width: 100%;
    max-width: 100%;
    margin: 0 0 12px 0 !important;
  }
  
  .intro-cover >>> .el-image {
    width: 100%;
    border-radius: 8px;
  }
  
  .intro-text {
    width: 100%;
  }
  
  .resource-description {
    font-size: 14px;
    line-height: 1.8;
    word-wrap: break-word;
    padding: 0 !important;
    margin: 0 !important;
  }
  
  /* 内容章节 */
  .content-section {
    margin: 16px 0 0 0 !important;
    padding: 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  .resource-content {
    font-size: 14px;
    line-height: 1.8;
    max-width: 100% !important;
    overflow-x: auto;
    word-wrap: break-word;
    padding: 12px !important;
    margin: 0 !important;
  }
  
  /* 下载区域 */
  .download-section {
    margin: 16px 0 0 0 !important;
    padding: 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  .download-content {
    padding: 12px !important;
    margin: 0 !important;
  }
  
  .share-download-btn {
    width: 100% !important;
    padding: 12px !important;
    font-size: 15px !important;
  }
  
  .file-list {
    margin-top: 12px;
  }
  
  .file-item {
    flex-direction: column;
    align-items: flex-start !important;
    padding: 12px !important;
    margin-bottom: 8px;
    max-width: 100%;
    word-break: break-all;
  }
  
  .file-info {
    margin: 8px 0 !important;
    width: 100%;
  }
  
  .file-name {
    font-size: 13px;
    word-break: break-all;
  }
  
  .file-download-btn {
    width: 100% !important;
    margin-top: 8px !important;
  }
  
  .download-tips {
    margin-top: 12px;
  }
  
  .tip-item {
    font-size: 12px;
    padding: 8px !important;
    margin: 4px 0;
  }
  
  /* 评论区域 */
  .comment-section {
    margin: 16px 0 0 0 !important;
    padding: 0 0 16px 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 图片优化 */
  .resource-content >>> img,
  .resource-description >>> img {
    max-width: 100% !important;
    height: auto !important;
    display: block;
    margin: 8px auto;
  }
  
  /* 表格优化 */
  .resource-content >>> table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    font-size: 12px;
  }
  
  /* 代码块优化 */
  .resource-content >>> pre,
  .resource-content >>> code {
    max-width: 100%;
    overflow-x: auto;
    word-wrap: break-word;
    font-size: 12px;
  }
  
  /* 按钮优化 */
  .download-btn,
  .like-btn,
  .share-btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}

/* ==================== 深色模式样式 ==================== */

/* 深色模式下覆盖 Element UI 文本按钮的全局样式 */
body.dark-mode .el-button--text.back-btn,
html.dark-mode .el-button--text.back-btn,
.dark-mode .el-button--text.back-btn {
  color: #90b0ff !important;
  background: rgba(102, 126, 234, 0.25) !important;
  border: 1px solid rgba(102, 126, 234, 0.5) !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2) !important;
  padding: 6px 12px !important;
  border-radius: 22px !important;
}

body.dark-mode .el-button--text.back-btn:hover,
html.dark-mode .el-button--text.back-btn:hover,
.dark-mode .el-button--text.back-btn:hover {
  color: #a0c0ff !important;
  background: rgba(102, 126, 234, 0.4) !important;
  border-color: rgba(102, 126, 234, 0.8) !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
  transform: translateX(-6px) !important;
}

.dark-mode .resource-detail {
  background: transparent;
}

.dark-mode .back-btn {
  color: #90b0ff !important; /* 使用更亮的颜色 */
  font-weight: 600; /* 增加字重 */
  background: rgba(102, 126, 234, 0.25) !important; /* 增加背景透明度 */
  border: 1px solid rgba(102, 126, 234, 0.5) !important; /* 增加边框透明度 */
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2) !important; /* 添加阴影 */
  padding: 6px 12px !important; /* 确保内边距 */
  border-radius: 22px !important; /* 确保圆角 */
}

.dark-mode .back-btn:hover {
  color: #a0c0ff !important; /* 更亮的悬停颜色 */
  background: rgba(102, 126, 234, 0.4) !important; /* 更明显的背景 */
  border-color: rgba(102, 126, 234, 0.8) !important; /* 更明显的边框 */
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important; /* 增强阴影效果 */
  transform: translateX(-6px) !important; /* 增加移动距离 */
}

/* 主要内容区 */
.dark-mode .detail-main {
  background: #2a2a2a;
  border-color: #404040;
}

.dark-mode .top-ribbon {
  background: linear-gradient(135deg, #d64545 0%, #c43d4f 100%);
  box-shadow: 0 4px 12px rgba(214, 69, 69, 0.5), 0 0 20px rgba(214, 69, 69, 0.2);
}

.dark-mode .resource-title {
  color: #e0e0e0;
  background: linear-gradient(135deg, #e0e0e0 0%, #b0b0b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dark-mode .meta-item {
  color: #b0b0b0;
  background: rgba(42, 42, 42, 0.6);
}

.dark-mode .meta-item:hover {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
}

.dark-mode .meta-item i {
  color: #909399;
}

/* 封面图片 */
.dark-mode .intro-cover {
  background: #1a1a1a;
}

.dark-mode .intro-text .resource-description {
  background: #1a1a1a;
  border-left-color: #667eea;
  color: #b0b0b0;
}

/* 资源下载区域 */
.dark-mode .download-content {
  background: #1a1a1a;
  border-color: #404040;
}

.dark-mode .download-content .file-item {
  background: #2a2a2a;
  border-color: #404040;
}

.dark-mode .download-content .file-item:hover {
  border-color: #667eea;
  background: #323232;
}

.dark-mode .download-content .file-name {
  color: #e0e0e0;
}

.dark-mode .download-tips .tip-item {
  background: #2a2a2a;
  color: #b0b0b0;
}

/* Tag标签深色模式 */
.dark-mode >>> .el-tag {
  background: rgba(102, 126, 234, 0.25);
  border-color: rgba(102, 126, 234, 0.4);
  color: #8ba3ff;
}

.dark-mode >>> .el-tag--success {
  background: rgba(103, 194, 58, 0.25);
  border-color: rgba(103, 194, 58, 0.4);
  color: #85d65a;
}

.dark-mode >>> .el-tag--warning {
  background: rgba(230, 162, 60, 0.25);
  border-color: rgba(230, 162, 60, 0.4);
  color: #f0b860;
}

.dark-mode >>> .el-tag--danger {
  background: rgba(245, 108, 108, 0.25);
  border-color: rgba(245, 108, 108, 0.4);
  color: #ff8080;
}

.dark-mode >>> .el-tag--info {
  background: rgba(144, 147, 153, 0.25);
  border-color: rgba(144, 147, 153, 0.4);
  color: #a8abb2;
}

/* 内容区域 */
.dark-mode .content-section {
  border-top-color: #404040;
}

.dark-mode .section-title {
  color: #e0e0e0;
  border-bottom-color: #404040;
}

/* 深色模式下的介绍区域标题 */
.dark-mode .intro-section .section-title {
  background: linear-gradient(#1a1a1a, #1a1a1a) padding-box,
              linear-gradient(90deg, #667eea 0%, #409EFF 100%) border-box;
  color: #e0e0e0;
}

.dark-mode .intro-section .section-title i {
  color: #667eea;
}

.dark-mode .resource-description,
.dark-mode .resource-content {
  color: #b0b0b0;
  background: #1a1a1a;
}

/* 富文本内容深色模式 */
.dark-mode .resource-content >>> h1,
.dark-mode .resource-content >>> h2,
.dark-mode .resource-content >>> h3,
.dark-mode .resource-content >>> h4,
.dark-mode .resource-content >>> h5,
.dark-mode .resource-content >>> h6 {
  color: #e0e0e0;
}

.dark-mode .resource-content >>> blockquote {
  background: #1a1a1a;
  border-left-color: #667eea;
  color: #b0b0b0;
}

.dark-mode .resource-content >>> code {
  background: #1a1a1a;
  color: #e0e0e0;
}

.dark-mode .resource-content >>> pre {
  background: #1a1a1a;
  border: 1px solid #404040;
}

.dark-mode .resource-description {
  background: #1a1a1a;
  border-left-color: #667eea;
  color: #b0b0b0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* 侧边栏 */
.dark-mode .download-card,
.dark-mode .tips-card {
  background: #2a2a2a;
  border-color: #404040;
}

/* 热门下载深色模式 */
.dark-mode .hot-download-card {
  background: linear-gradient(135deg, #3a2626 0%, #2d1f1f 100%);
  border-color: rgba(255, 87, 87, 0.3);
}

.dark-mode .hot-download-card::before {
  background: radial-gradient(circle, rgba(255, 87, 87, 0.15) 0%, transparent 70%);
}

.dark-mode .card-title {
  color: #ff7a7a;
}

.dark-mode .hot-download-item {
  background: rgba(42, 42, 42, 0.8);
  border-color: transparent;
}

.dark-mode .hot-download-item:hover {
  background: rgba(58, 38, 38, 0.9);
  border-color: #ff5757;
  box-shadow: 0 6px 20px rgba(255, 87, 87, 0.3);
}

.dark-mode .hot-download-item::before {
  background: linear-gradient(180deg, #ff5757 0%, #ff8787 100%);
}

.dark-mode .title-text {
  color: #e0e0e0;
}

.dark-mode .hot-download-item:hover .title-text {
  color: #ff7a7a;
}

.dark-mode .hot-tag {
  background: linear-gradient(135deg, #ff5757 0%, #ff8787 100%);
  box-shadow: 0 2px 6px rgba(255, 87, 87, 0.4);
}

.dark-mode .hot-item-meta {
  color: #909399;
}

.dark-mode .hot-item-meta .download-count {
  color: #ff7a7a;
}

.dark-mode .card-header {
  color: #e0e0e0;
  border-bottom-color: #404040;
}

/* 文件列表 */
.dark-mode .file-item {
  background: #1a1a1a;
  border-color: #404040;
}

.dark-mode .file-item:hover {
  background: #323232;
  border-color: #667eea;
}

.dark-mode .file-name {
  color: #e0e0e0;
}

.dark-mode .file-size {
  color: #909399;
}

/* 空状态 */
.dark-mode >>> .el-empty__description {
  color: #909399;
}

.dark-mode >>> .el-empty__image svg {
  fill: #404040;
}

/* 温馨提示深色模式 */
.dark-mode .tips-card {
  background: linear-gradient(135deg, #3a3420 0%, #2d2818 100%);
  border-color: rgba(230, 162, 60, 0.3);
}

.dark-mode .tips-title {
  color: #E6A23C;
}

.dark-mode .tips-list li {
  color: #b0b0b0;
}

/* 按钮 */
.dark-mode >>> .el-button--primary {
  background: #667eea;
  border-color: #667eea;
}

.dark-mode >>> .el-button--primary:hover {
  background: #7e92f0;
  border-color: #7e92f0;
}

.dark-mode >>> .el-button--text {
  color: #667eea;
}

.dark-mode >>> .el-button--text:hover {
  color: #7e92f0;
}

/* 深色模式下的标签容器 */
.dark-mode .fixed-tags,
.dark-mode .user-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.dark-mode .fixed-tags {
  justify-content: flex-start; /* 左对齐 */
}

.dark-mode .user-tags {
  justify-content: flex-end; /* 右对齐 */
}

/* 版权声明深色模式 */
.dark-mode .copyright-card {
  background: linear-gradient(135deg, #1f2a3a 0%, #182030 100%);
  border-color: rgba(64, 158, 255, 0.3);
}

.dark-mode .copyright-title {
  color: #667eea;
}

.dark-mode .copyright-item {
  color: #b0b0b0;
}

.dark-mode .copyright-item strong {
  color: #e0e0e0;
}

.dark-mode .copyright-item >>> .el-link {
  color: #66b3ff !important;
}

.dark-mode .copyright-item >>> .el-link:hover {
  color: #90c8ff !important;
}

.dark-mode .copyright-welcome {
  background: rgba(102, 126, 234, 0.15);
  border-color: #667eea;
  color: #667eea;
}

/* 互动区域深色模式 */
.dark-mode .interaction-section {
  border-top-color: #404040;
  background: #2a2a2a;
}

.dark-mode .support-tip {
  background: rgba(64, 158, 255, 0.15);
  border-color: #409EFF;
  color: #409EFF;
  /* 确保容器本身可以接收点击事件 */
  pointer-events: auto;
}

.dark-mode .support-tip:hover {
  background: rgba(64, 158, 255, 0.2);
}

.dark-mode .support-text {
  color: #a0c8ff;
}

.dark-mode .support-content {
  /* 确保内容区域不影响按钮点击 */
  pointer-events: none;
}

.dark-mode .support-content > * {
  /* 重新启用子元素的点击事件 */
  pointer-events: auto;
}

.dark-mode .support-subtext {
  color: #8cb3e6;
}

.dark-mode .support-icon {
  color: #66b3ff;
}

.dark-mode .upgrade-btn {
  background: linear-gradient(135deg, #667eea 0%, #409EFF 100%) !important;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  /* 确保按钮在最上层 */
  z-index: 10;
  position: relative;
}

.dark-mode .upgrade-btn:hover {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* 相关推荐深色模式 */
/* 移除深色模式下的上方横线样式 */
/* .dark-mode .related-section {
  border-top-color: #404040;
} */

.dark-mode .action-bar {
  /* 确保与上方元素有足够的间距 */
  margin-top: 20px;
  position: relative;
  z-index: 1;
}

.dark-mode .related-item {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  border-color: transparent;
}

.dark-mode .related-item:hover {
  background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
  border-color: #667eea;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
}

.dark-mode .related-cover {
  background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
}

.dark-mode .related-cover .cover-placeholder {
  color: #606266;
}

.dark-mode .related-title {
  color: #e0e0e0;
}

.dark-mode .related-desc {
  color: #b0b0b0;
}

.dark-mode .related-meta {
  color: #909399;
}

.dark-mode .related-meta .meta-tag {
  background: #323232;
}

.dark-mode .related-item:hover .related-meta .meta-tag {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
}

/* 评论区域 */
.comment-section {
  margin-top: 35px;
  padding-top: 30px;
  border-top: 2px solid #f0f2f5;
}

.comment-input-box {
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-radius: 12px;
  border: 2px solid #e8eaf0;
}

.comment-input-actions {
  margin: 12px 0;
  position: relative;
  display: flex;
  align-items: center;
}

.emoji-toggle-btn {
  color: #909399;
  font-size: 18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e8eaf0;
  background: white;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  cursor: pointer;
  flex-shrink: 0;
}

.emoji-toggle-btn:hover {
  color: #409EFF;
  background: #f0f8ff;
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
  transform: rotate(15deg) scale(1.1);
}

.emoji-toggle-btn.active {
  color: #409EFF;
  background: #e8f4ff;
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  transform: rotate(15deg) scale(1.1);
}

.emoji-picker {
  position: absolute;
  top: 30px;
  left: 0;
  background: white;
  border: 2px solid #e8eaf0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  padding: 10px;
  z-index: 1000;
  max-width: 300px;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 5px;
}

.emoji-item {
  font-size: 18px;
  text-align: center;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s;
}

.emoji-item:hover {
  background: #f0f2f5;
  transform: scale(1.2);
}

.comment-actions {
  margin-top: 12px;
  text-align: right;
}

.comment-list {
  min-height: 200px;
}

.empty-comments {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-comments i {
  font-size: 60px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-comments p {
  font-size: 14px;
  margin: 0;
}

.comment-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  border: 2px solid #f0f2f5;
  transition: all 0.3s;
}

.comment-item:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.1);
}

.comment-avatar {
  flex-shrink: 0;
}

.comment-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.comment-content-wrapper {
  flex: 1;
  min-width: 0;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.comment-user {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.comment-time {
  font-size: 12px;
  color: #909399;
}

.comment-content {
  color: #606266;
  line-height: 1.6;
  font-size: 14px;
  margin-bottom: 8px;
  word-wrap: break-word;
}

.comment-actions-bar {
  display: flex;
  gap: 20px;
}

.comment-action {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.comment-action:hover {
  color: #409EFF;
}

.comment-action i {
  font-size: 14px;
}

/* 回复输入框 */
.reply-input-box {
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.reply-input-actions {
  margin: 12px 0;
  position: relative;
  display: flex;
  align-items: center;
}

.reply-actions {
  margin-top: 8px;
  text-align: right;
}

/* 回复列表 */
.reply-list {
  margin-top: 16px;
  padding-left: 20px;
  border-left: 3px solid #e8eaf0;
}

.reply-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
}

.reply-avatar img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.reply-content-wrapper {
  flex: 1;
  min-width: 0;
}

.reply-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.reply-user {
  font-weight: 600;
  color: #606266;
  font-size: 13px;
}

.reply-time {
  font-size: 12px;
  color: #b0b0b0;
}

.reply-content {
  color: #606266;
  line-height: 1.6;
  font-size: 13px;
  word-wrap: break-word;
}

/* 评论分页 */
.comment-pagination {
  margin-top: 30px;
  text-align: center;
}

/* 深色模式 - 评论区域 */
.dark-mode .comment-section {
  border-top-color: #404040;
}

.dark-mode .comment-input-box {
  background: #1a1a1a;
  border-color: #404040;
}

.dark-mode .comment-item {
  background: #2a2a2a;
  border-color: #404040;
}

.dark-mode .comment-item:hover {
  border-color: #667eea;
}

.dark-mode .comment-user {
  color: #e0e0e0;
}

.dark-mode .comment-content {
  color: #b0b0b0;
}

.dark-mode .reply-input-box {
  background: #323232;
}

.dark-mode .reply-item {
  background: #323232;
}

.dark-mode .reply-user {
  color: #c0c0c0;
}

.dark-mode .reply-content {
  color: #b0b0b0;
}

/* 深色模式 - 下载区域 */
.dark-mode .download-content {
  background: #1a1a1a;
  border-color: #404040;
}

.dark-mode .download-content .file-item {
  background: #2a2a2a;
  border-color: #404040;
}

.dark-mode .download-content .file-item:hover {
  border-color: #667eea;
  background: #323232;
}

.dark-mode .download-content .file-name {
  color: #e0e0e0;
}

.dark-mode .download-tips .tip-item {
  background: #2a2a2a;
  color: #b0b0b0;
}

/* 深色模式 - 表情选择器 */
.dark-mode .emoji-picker {
  background: #2a2a2a;
  border-color: #404040;
}

.dark-mode .emoji-item:hover {
  background: #323232;
}

.dark-mode .emoji-toggle-btn {
  background: #2a2a2a;
  border-color: #404040;
  color: #909399;
}

.dark-mode .emoji-toggle-btn:hover {
  background: #323232;
  border-color: #667eea;
  color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.dark-mode .emoji-toggle-btn.active {
  background: #323232;
  border-color: #667eea;
  color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* 资源详情淡入淡出动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
<style>
/* Emoji 弹框专用样式（全局，确保 append-to-body 可生效） */
.emoji-dialog { max-width: 640px; }
.emoji-dialog .el-dialog__body { padding: 8px 12px; }
.emoji-dialog .emoji-picker {
  position: static;
  top: auto;
  left: auto;
  max-height: 320px;
  overflow-y: auto;
  border: none;
  box-shadow: none;
  padding: 6px;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
}
.emoji-dialog .emoji-item { font-size: 22px; padding: 8px; border-radius: 6px; }
.emoji-dialog .emoji-item:hover { background: #f0f2f5; transform: scale(1.08); }
@media (max-width: 768px) {
  .emoji-dialog { width: 92% !important; }
  .emoji-dialog .emoji-picker { grid-template-columns: repeat(8, 1fr); max-height: 260px; }
}
.dark-mode .emoji-dialog .emoji-picker { background: #2a2a2a; }
.dark-mode .emoji-dialog .emoji-item:hover { background: #323232; }

/* Emoji 气泡弹层样式（跟随按钮，稳定栅格与滚动） */
.emoji-popover { max-width: 640px; padding: 8px !important; box-sizing: border-box; overflow-x: hidden; }
.emoji-popover .emoji-picker {
  position: static;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  grid-auto-rows: 36px;
  gap: 8px;
}
.emoji-popover .emoji-item { font-size: 22px; width: 32px; height: 32px; line-height: 32px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 6px; box-sizing: border-box; }
.emoji-popover .emoji-item:hover { background: #f0f2f5; transform: scale(1.02); }
@media (max-width: 768px) {
  .emoji-popover { width: 92% !important; }
  .emoji-popover .emoji-picker { grid-template-columns: repeat(8, 1fr); grid-auto-rows: 34px; max-height: 260px; }
}
.dark-mode .emoji-popover { background: #2a2a2a; border-color: #404040; }
.dark-mode .emoji-popover .emoji-item:hover { background: #323232; }
</style>
